import {
  AccessDeniedError,
  BadRequestError,
  ConnectionError,
  HttpTypes,
  InternalServerError,
  TooManyRequestsError,
  UnexpectedError,
} from 'src/http';
import { httpResponseHandler } from 'src/http/http-response-handler';
import { collectibleType, collectionPeriodType, collectionType } from 'src/types/collectibles';
import { endpoints } from 'src/utils/axios';
import { Either } from 'src/utils/either';

export class CollectiblesRepository {
  constructor(private api: HttpTypes.HttpClient) {}

  async getCollectionsList(): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: endpoints.benefits.collectibles.collectionList,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getCollectionDetails(
    id: string
  ): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: endpoints.benefits.collectibles.collectionDetails(id),
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getCollectionPeriods(): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | collectionPeriodType[]
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: endpoints.benefits.collectibles.collectionPeriods,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async createCollection(
    collection: any
  ): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: endpoints.benefits.collectibles.createCollection,
      body: collection,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async updateCollection(
    id: string,
    collection: collectionType
  ): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'put',
      url: endpoints.benefits.collectibles.updateCollection,
      params: { id },
      body: collection,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async createCollectible(
    id: string,
    collectible: collectibleType
  ): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: endpoints.benefits.collectibles.createCollectible,
      params: { id },
      body: {
        ...collectible,
        type: 'free',
      },
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getCollectibleDetails(
    id: string
  ): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: endpoints.benefits.collectibles.collectibleDetails(id),
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getLocations(): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `benefits/store/location/all`,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getCollectiblesList(
    collectionId: string
  ): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: endpoints.benefits.collectibles.collectiblesList(collectionId),
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async updateCollectible(
    id: string,
    collectible: collectibleType
  ): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'put',
      url: endpoints.benefits.collectibles.updateCollectible,
      params: { id },
      body: collectible,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async updateCollectibleType(
    collectibleId: string,
    type: collectibleType['type']
  ): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'put',
      url: endpoints.benefits.collectibles.updateCollectibleType,
      body: {
        collectibleId,
        type,
      },
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async exportCollectibles(
    ids: string[],
    userName: string
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: endpoints.benefits.collectibles.exportData,
      body: { ids, userName },
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }
}
